import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import type { TFunction } from 'i18next'
import { Icon } from '../../../atoms/Icon'
import { InfoTooltip } from '../../../InfoTooltip'
import { Loading } from '../../../atoms/Loading'
import {
  IDCComparisons,
  IDCComparisonScopeType,
  calculatePercentileRank,
  getPerformanceCategory,
} from '@popety-io/types'
import { IDCDistributionChart } from './IDCDistributionChart'

interface IDCComparisonCardProps {
  buildingId: string
  idc: number // MJ/m²·year
  buildingYear?: number
  t: TFunction
  onFetchComparisons: (buildingId: string) => Promise<IDCComparisons>
}

const Card = styled.div`
  background: ${({ theme }) => theme.colors.background.primary};
  border-radius: ${({ theme }) => theme.borderRadius.large};
  padding: 20px;
  box-shadow: ${({ theme }) => theme.shadows.sm};
`

const CardTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSize.section};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 16px 0;
  display: flex;
  align-items: center;
  gap: 6px;
`

const ScopeSelector = styled.div`
  display: flex;
  gap: 6px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`

const ScopeButton = styled.button<{ $active: boolean }>`
  padding: 6px 12px;
  border-radius: ${({ theme }) => theme.borderRadius.default};
  border: 1px solid
    ${({ theme, $active }) =>
      $active ? theme.colors.primary : theme.colors.border.default};
  background: ${({ theme, $active }) =>
    $active ? theme.colors.primary + '15' : theme.colors.background.secondary};
  color: ${({ theme, $active }) =>
    $active ? theme.colors.primary : theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  font-weight: ${({ theme, $active }) =>
    $active
      ? theme.typography.fontWeight.semibold
      : theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    background: ${({ theme }) => theme.colors.primary + '20'};
  }
`

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
  margin-bottom: 12px;
`

const StatItem = styled.div`
  padding: 10px;
  background: ${({ theme }) => theme.colors.background.secondary};
  border-radius: ${({ theme }) => theme.borderRadius.default};
`

const StatLabel = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: 3px;
`

const StatValue = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
`

const PerformanceBanner = styled.div<{ $color: string }>`
  padding: 12px;
  background: ${({ $color }) => $color}15;
  border-left: 4px solid ${({ $color }) => $color};
  border-radius: ${({ theme }) => theme.borderRadius.default};
  margin-bottom: 12px;
`

const PerformanceTitle = styled.div<{ $color: string }>`
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ $color }) => $color};
  margin-bottom: 3px;
  display: flex;
  align-items: center;
  gap: 6px;
`

const PerformanceText = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: 1.5;
`

const NoDataText = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-style: italic;
  text-align: center;
  padding: 12px;
`

const ErrorText = styled.div`
  padding: 12px;
  background: #ef444415;
  border-left: 4px solid #ef4444;
  border-radius: ${({ theme }) => theme.borderRadius.default};
  color: #ef4444;
  font-size: ${({ theme }) => theme.typography.fontSize.small};
`

export const IDCComparisonCard: React.FC<IDCComparisonCardProps> = ({
  buildingId,
  idc,
  t,
  onFetchComparisons,
}) => {
  const getScopeLabel = (scope: IDCComparisonScopeType) => {
    return t(`energy.idcComparisonCard.scopeLabels.${scope}`)
  }
  const [comparisons, setComparisons] = useState<IDCComparisons | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedScope, setSelectedScope] =
    useState<IDCComparisonScopeType>('radius_500m')

  useEffect(() => {
    const fetchComparisons = async () => {
      try {
        setLoading(true)
        setError(null)
        const data = await onFetchComparisons(buildingId)
        setComparisons(data)

        // Auto-select first available scope
        const availableScopes = Object.keys(
          data.comparisons
        ) as IDCComparisonScopeType[]
        if (availableScopes.length > 0) {
          setSelectedScope(availableScopes[0])
        }
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Failed to load comparisons'
        )
      } finally {
        setLoading(false)
      }
    }

    fetchComparisons()
  }, [buildingId, onFetchComparisons])

  if (loading) {
    return (
      <Card>
        <CardTitle>
          {t('idcComparisonCard.title')}
          <InfoTooltip>
            <span>{t('idcComparisonCard.tooltipDescriptionLoading')}</span>
          </InfoTooltip>
        </CardTitle>
        <div style={{ padding: '32px', textAlign: 'center' }}>
          <Loading size="medium" />
        </div>
      </Card>
    )
  }

  if (error) {
    return (
      <Card>
        <CardTitle>{t('idcComparisonCard.title')}</CardTitle>
        <ErrorText>{error}</ErrorText>
      </Card>
    )
  }

  if (!comparisons || Object.keys(comparisons.comparisons).length === 0) {
    return (
      <Card>
        <CardTitle>{t('idcComparisonCard.title')}</CardTitle>
        <NoDataText>{t('idcComparisonCard.noData')}</NoDataText>
      </Card>
    )
  }

  const selectedComparison = comparisons.comparisons[selectedScope]
  const availableScopes = Object.keys(
    comparisons.comparisons
  ) as IDCComparisonScopeType[]

  if (!selectedComparison) {
    return (
      <Card>
        <CardTitle>{t('idcComparisonCard.title')}</CardTitle>
        <NoDataText>{t('idcComparisonCard.noDataForZone')}</NoDataText>
      </Card>
    )
  }

  const { statistics } = selectedComparison
  const percentileRank = calculatePercentileRank(idc, statistics)
  const performance = getPerformanceCategory(percentileRank)

  return (
    <Card>
      <CardTitle>
        {t('idcComparisonCard.title')}
        <InfoTooltip>
          <span>{t('idcComparisonCard.tooltipDescription')}</span>
        </InfoTooltip>
      </CardTitle>

      <ScopeSelector>
        {availableScopes.map((scope) => (
          <ScopeButton
            key={scope}
            $active={selectedScope === scope}
            onClick={() => setSelectedScope(scope)}
          >
            {getScopeLabel(scope)}
          </ScopeButton>
        ))}
      </ScopeSelector>

      <PerformanceBanner $color={performance.color}>
        <PerformanceTitle $color={performance.color}>
          <Icon name="award" size="small" />
          {performance.label}
        </PerformanceTitle>
        <PerformanceText>
          {t('idcComparisonCard.performanceText', {
            idc: String(Math.round(idc)),
            percentile: String(Math.round(percentileRank)),
            count: statistics.count.toLocaleString(),
            scope: selectedComparison.scope_label.toLowerCase(),
          } as Record<string, string>)}
        </PerformanceText>
      </PerformanceBanner>

      {/* Distribution Chart */}
      {selectedComparison.distribution &&
        selectedComparison.distribution.length > 0 && (
          <IDCDistributionChart
            distribution={selectedComparison.distribution}
            buildingIDC={idc}
            scopeLabel={selectedComparison.scope_label}
            t={t}
          />
        )}

      <StatsGrid>
        <StatItem>
          <StatLabel>{t('idcComparisonCard.stats.median')}</StatLabel>
          <StatValue>{Math.round(statistics.median)} MJ/m²</StatValue>
        </StatItem>
        <StatItem>
          <StatLabel>{t('idcComparisonCard.stats.average')}</StatLabel>
          <StatValue>{Math.round(statistics.avg)} MJ/m²</StatValue>
        </StatItem>
        <StatItem>
          <StatLabel>{t('idcComparisonCard.stats.percentile25')}</StatLabel>
          <StatValue>{Math.round(statistics.percentile_25)} MJ/m²</StatValue>
        </StatItem>
        <StatItem>
          <StatLabel>{t('idcComparisonCard.stats.percentile75')}</StatLabel>
          <StatValue>{Math.round(statistics.percentile_75)} MJ/m²</StatValue>
        </StatItem>
        <StatItem>
          <StatLabel>{t('idcComparisonCard.stats.minimum')}</StatLabel>
          <StatValue>{Math.round(statistics.min)} MJ/m²</StatValue>
        </StatItem>
        <StatItem>
          <StatLabel>{t('idcComparisonCard.stats.maximum')}</StatLabel>
          <StatValue>{Math.round(statistics.max)} MJ/m²</StatValue>
        </StatItem>
      </StatsGrid>
    </Card>
  )
}
